import { useCallback, useContext } from 'react';
import server from 'utils/server';
import firebase, { storageRef } from 'utils/firebase';
import { AuthContext } from 'Contexts/AuthContext';
import { RestaurantContext } from 'Contexts/RestaurantContext';
import useLanguage from 'CustomHooks/useLanguage';
import { SupportAttachmentBlock } from 'types/shared/documents/support';
import {
  CreateConversationRequest,
  CreateConversationResponse,
  CreateTicketRequest,
  CreateTicketResponse,
  PostMessageInput,
  PostMessageResponse,
  RestaurantTicketsResponse,
  RestaurantTicketSummary,
  TemplatesResponse,
  UpdateConversationRequest,
} from 'types/shared/routes/v03/support';

type CreateConversationInput = Omit<
  CreateConversationRequest,
  'uid' | 'restaurantId'
>;

type CreateTicketInput = Omit<CreateTicketRequest, 'uid' | 'restaurantId'>;

/**
 * Write side of the support system. Reads come straight from Firestore
 * (see useSupportData; tickets are fetched via the API there, as their docs are
 * internal); every write goes through `/v03/support/*`, which is the
 * only path allowed to touch the support* collections. `uid` is sent in the
 * body because the dashboard does not set the axios `uid` header reliably.
 */
const useSupportApi = () => {
  const { uid } = useContext(AuthContext);
  const { restaurantId, products } = useContext(RestaurantContext);
  const { language } = useLanguage();

  const baseBody = useCallback(
    () => ({ uid: uid || '', restaurantId: restaurantId || '' }),
    [uid, restaurantId],
  );

  // Fail fast instead of posting empty identifiers if a write is triggered
  // before the auth/restaurant context is ready.
  const requireAuth = useCallback(() => {
    if (!uid || !restaurantId) {
      throw new Error('Support: uid oder restaurantId fehlt.');
    }
  }, [uid, restaurantId]);

  const getTemplates = useCallback(async () => {
    // Skip the call until auth is ready — an empty uid just 401s. The effect
    // refetches when uid arrives, since getTemplates changes with it.
    if (!uid) return [];
    // GET carries no body, and the axios `uid` header is unset, so the API's
    // auth check needs uid passed explicitly per request.
    const { data } = await server.get<TemplatesResponse>(
      '/v03/support/templates',
      { headers: { uid } },
    );
    return data.templates;
  }, [uid]);

  const listTickets = useCallback(async (): Promise<
    RestaurantTicketSummary[]
  > => {
    if (!uid || !restaurantId) return [];
    // Ticket docs are internal — unlike the sibling calls this route only
    // accepts a verified ID token, not the claimed uid header.
    const token = await firebase.auth().currentUser?.getIdToken();
    if (!token) return [];
    const { data } = await server.get<RestaurantTicketsResponse>(
      '/v03/support/tickets',
      {
        params: { restaurantId },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return data.tickets;
  }, [uid, restaurantId]);

  const createConversation = useCallback(
    async (input: CreateConversationInput = {}) => {
      requireAuth();
      const { data } = await server.post<CreateConversationResponse>(
        '/v03/support/conversations',
        {
          ...baseBody(),
          channel: 'dashboard',
          metadata: {
            language,
            products,
            page: window.location.pathname,
            userAgent: navigator.userAgent,
          },
          ...input,
        },
      );
      return data.conversation;
    },
    [baseBody, requireAuth, language, products],
  );

  const postMessage = useCallback(
    async (conversationId: string, input: PostMessageInput) => {
      requireAuth();
      const { data } = await server.post<PostMessageResponse>(
        `/v03/support/conversations/${conversationId}/messages`,
        { ...baseBody(), ...input },
      );
      return data.message;
    },
    [baseBody, requireAuth],
  );

  const setTyping = useCallback(
    (conversationId: string, typing: boolean) => {
      requireAuth();
      return server.post(
        `/v03/support/conversations/${conversationId}/typing`,
        { ...baseBody(), typing },
      );
    },
    [baseBody, requireAuth],
  );

  const createTicket = useCallback(
    async (input: CreateTicketInput) => {
      requireAuth();
      const { data } = await server.post<CreateTicketResponse>(
        '/v03/support/tickets',
        { ...baseBody(), ...input },
      );
      return data.ticket;
    },
    [baseBody, requireAuth],
  );

  const updateConversation = useCallback(
    async (
      conversationId: string,
      input: Omit<UpdateConversationRequest, 'uid' | 'restaurantId'>,
    ) => {
      requireAuth();
      await server.post(`/v03/support/conversations/${conversationId}/update`, {
        ...baseBody(),
        ...input,
      });
    },
    [baseBody, requireAuth],
  );

  const closeConversation = useCallback(
    (conversationId: string) =>
      updateConversation(conversationId, { status: 'closed' }),
    [updateConversation],
  );

  const markConversationRead = useCallback(
    async (conversationId: string) => {
      requireAuth();
      await server.post(
        `/v03/support/conversations/${conversationId}/read`,
        baseBody(),
      );
    },
    [baseBody, requireAuth],
  );

  const rateMessage = useCallback(
    async (
      conversationId: string,
      messageId: string,
      dir: 'up' | 'down' | null,
    ) => {
      requireAuth();
      await server.post(
        `/v03/support/conversations/${conversationId}/messages/${messageId}/thumb`,
        { ...baseBody(), dir },
      );
    },
    [baseBody, requireAuth],
  );

  // Upload an image/video to Storage and return an attachment block to send.
  const uploadAttachment = useCallback(
    async (file: File): Promise<SupportAttachmentBlock> => {
      requireAuth();
      const safeName = file.name.replace(/[^\w.\-]+/g, '_');
      const fileRef = storageRef.child(
        `/restaurants/${restaurantId}/support/${Date.now()}-${safeName}`,
      );
      await fileRef.put(file);
      const url = await fileRef.getDownloadURL();
      return {
        type: 'attachment',
        url,
        name: file.name,
        mimeType: file.type,
        size: file.size,
      };
    },
    [requireAuth, restaurantId],
  );

  return {
    getTemplates,
    listTickets,
    createConversation,
    postMessage,
    setTyping,
    createTicket,
    updateConversation,
    closeConversation,
    markConversationRead,
    rateMessage,
    uploadAttachment,
  };
};

export default useSupportApi;
